import { X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import coin from "../assets/images/placeholder/crypto-colour.png";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import AssetSelectionContent from "./AssetSelectionContent";

interface Asset {
  assetId: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  availableAmount?: number;
  availableAmountFormatted?: string;
  valueInLocalCurrency?: string;
  [key: string]: unknown;
}

interface AssetSelectionDialogProps {
  selectedAsset: Asset | null | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filteredAssets: Asset[] | undefined;
  onAssetSelect: (asset: Asset) => void;
  disabled?: boolean;
  showBalance?: boolean;
  formatter?: {
    format: (amount: number | undefined, currency: string) => string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
}

export default function AssetSelectionDialog({
  selectedAsset,
  searchTerm,
  onSearchChange,
  filteredAssets,
  onAssetSelect,
  disabled = false,
  showBalance = false,
  formatter,
  open,
  onOpenChange,
  isLoading = false,
}: AssetSelectionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center">
              {selectedAsset ? (
                <img
                  src={selectedAsset.assetIcon}
                  alt={selectedAsset.assetName ?? ""}
                  width={32}
                  height={32}
                  className="text-white"
                />
              ) : (
                <img
                  src={coin}
                  alt="Default Asset"
                  width={32}
                  height={32}
                  className="text-white"
                />
              )}
            </div>
            <div className="text-left">
              <div className="font-semibold">
                {selectedAsset?.asset.toUpperCase()}
              </div>
              <div className="text-text-secondary">
                {selectedAsset?.assetName ?? "Select..."}
              </div>
            </div>
          </div>
          <div className="border border-neutral rounded-full p-3">
            <ChevronDown className="text-text-secondary" />
          </div>
        </Button>
      </DialogTrigger>

      <DialogContent
        className="
      max-w-lg w-full rounded-2xl p-6
      sm:max-w-lg
      max-h-[90vh] overflow-y-auto
    "
      >
        <DialogClose asChild>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-neutral hover:bg-gray-100"
          >
            <X size={18} className="text-gray-700" />
          </Button>
        </DialogClose>

        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-800">
            Select an Asset
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 sm:mt-6">
          <AssetSelectionContent
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            filteredAssets={filteredAssets}
            onAssetSelect={onAssetSelect}
            showBalance={showBalance}
            formatter={formatter}
            isLoading={isLoading}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
